import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import VipIcon from '../Icons/VipIcons';
import { InlineLoading } from '../LoadingSpinner/LoadingSpinner';
import { PrimaryButton, SecondaryButton, UnstyledButton, IconButton } from '../common/Button';
import { ffmpegTrim } from '../../utils/ffmpegClient';

/**
 * VibeTrimmer
 * ---------------------------------------------------------------
 * Fixed-selection trimmer: a stationary highlight band is pinned
 * to the viewport with decorative handles at both ends; the video
 * filmstrip scrolls left/right underneath. The selected clip's
 * length equals the viewport-width in seconds (zoom controls it).
 * Preview loops the selected window; on confirm we re-encode just
 * that window to a Blob using MediaRecorder + captureStream so the
 * existing upload path can push a valid ≤MAX_SECONDS clip.
 *
 * Pure frontend — no backend changes required.
 */

const MIN_SECONDS = 1;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.85);
    backdrop-filter: blur(4px);
    z-index: 10000010;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
`;

const Panel = styled.div`
    background: ${({ theme }) => theme.colors?.bg2 || '#111'};
    color: ${({ theme }) => theme.colors?.text || '#e5e7eb'};
    border-radius: 16px;
    width: 100%;
    max-width: 520px;
    max-height: 96vh;
    overflow-y: auto;
    padding: 18px 20px 20px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
`;

const Title = styled.h3`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
`;

const VideoFrame = styled.div`
    width: 100%;
    aspect-ratio: 9 / 16;
    max-height: 55vh;
    background: #000;
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;
    position: relative;

    video {
        width: 100%;
        height: 100%;
        object-fit: contain;
        position: relative;
        z-index: 1;
    }
`;

const VideoBackdrop = styled.video`
    position: absolute !important;
    inset: -20px !important;
    width: calc(100% + 40px) !important;
    height: calc(100% + 40px) !important;
    object-fit: cover !important;
    z-index: 0 !important;
    filter: blur(28px) brightness(0.55) saturate(1.2);
    transform: scale(1.15);
    pointer-events: none;
`;

/**
 * Center-overlay play/pause toggle. Sits on top of the video and only
 * shows chrome when paused or hovered so it doesn't distract from the
 * preview while playing.
 */
const PlayPauseBtn = styled(UnstyledButton)`
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    transition: background 0.15s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.2);
    }

    /* Circular pill behind the icon so it's readable on any frame. */
    & > span {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.55);
        backdrop-filter: blur(6px);
        opacity: ${({ $paused }) => ($paused ? 1 : 0)};
        transform: scale(${({ $paused }) => ($paused ? 1 : 0.85)});
        transition: opacity 0.15s ease, transform 0.15s ease;
    }

    &:hover > span {
        opacity: 1;
        transform: scale(1);
    }
`;

const TimelineViewport = styled.div`
    position: relative;
    width: 100%;
    margin: 8px 0 4px;
    padding: 20px 0 10px;
`;

const TimelineScroll = styled.div`
    position: relative;
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    cursor: grab;

    &:active { cursor: grabbing; }

    /* Firefox */
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.colors?.divider || '#374151'} transparent;
    /* WebKit */
    &::-webkit-scrollbar { height: 8px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.colors?.divider || '#374151'};
        border-radius: 4px;
    }
`;

const Timeline = styled.div`
    position: relative;
    height: 44px;
    background: ${({ theme }) => theme.colors?.background || '#000'};
    border-radius: 10px;
    user-select: none;
    touch-action: pan-x;
    min-width: 100%;
    overflow: hidden;
`;

/**
 * Filmstrip layer inside Timeline. Thumbnails are absolutely positioned
 * at time*pxPerSec with width = perThumbSec*pxPerSec so they stay aligned
 * with the ruler as the user zooms or scrolls.
 */
const Filmstrip = styled.div`
    position: absolute;
    inset: 0;
    pointer-events: none;
`;

const Thumb = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.85;
`;

/**
 * Fixed selection overlay — pinned above the scrollable timeline, spans the
 * whole viewport width. Handles here are purely visual (no drag) since the
 * *timeline* moves under a stationary selection now.
 */
const SelectionOverlay = styled.div`
    position: absolute;
    top: 20px;
    left: 0;
    right: 0;
    height: 44px;
    pointer-events: none;
    box-sizing: border-box;
`;

const SelectionBox = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => theme.colors?.primary || 'rgba(236, 72, 153, 0.28)'}22;
    border-top: 2px solid ${({ theme }) => theme.colors?.primary || '#ec4899'};
    border-bottom: 2px solid ${({ theme }) => theme.colors?.primary || '#ec4899'};
    box-sizing: border-box;
`;

const FixedHandle = styled.div`
    position: absolute;
    top: -4px;
    bottom: -4px;
    width: 18px;
    border-radius: 6px;
    background: ${({ theme }) => theme.colors?.primary || '#ec4899'};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.35);
`;

const Ruler = styled.div`
    position: absolute;
    top: -18px;
    left: 0;
    right: 0;
    height: 16px;
    pointer-events: none;
    font-size: 9px;
    color: ${({ theme }) => theme.colors?.textSecondary || '#9ca3af'};
`;

const RulerTick = styled.div`
    position: absolute;
    top: 0;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;

    &::after {
        content: '';
        display: block;
        width: 1px;
        height: 6px;
        background: ${({ theme }) => theme.colors?.divider || '#374151'};
    }
`;

const ZoomBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 6px;
    font-size: 11px;
    color: ${({ theme }) => theme.colors?.textSecondary || '#9ca3af'};
`;

const ZoomBtn = styled(UnstyledButton)`
    width: 28px;
    height: 24px;
    border-radius: 6px;
    border: 1px solid ${({ theme }) => theme.colors?.divider || '#374151'};
    font-size: 14px;
    line-height: 1;
    &:hover { border-color: ${({ theme }) => theme.colors?.primary || '#ec4899'}; }
    &:disabled { opacity: 0.4; }
`;

const FitBtn = styled(ZoomBtn)`
    width: auto;
    padding: 0 8px;
    font-size: 11px;
`;

const LengthRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 8px;
    font-size: 11px;
    color: ${({ theme }) => theme.colors?.textSecondary || '#9ca3af'};
`;

const LengthSlider = styled.input.attrs({ type: 'range' })`
    flex: 1;
    accent-color: ${({ theme }) => theme.colors?.primary || '#ec4899'};
    cursor: pointer;
`;

const LengthValue = styled.span`
    min-width: 52px;
    text-align: right;
    color: ${({ theme }) => theme.colors?.text || '#e5e7eb'};
    font-variant-numeric: tabular-nums;
`;

const Playhead = styled.div`
    position: absolute;
    top: -6px;
    bottom: -6px;
    width: 2px;
    margin-left: -1px;
    background: white;
    pointer-events: none;
    opacity: 0.85;
`;

const Meta = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: ${({ theme }) => theme.colors?.textSecondary || '#9ca3af'};
    margin-top: 2px;
`;

const ActionRow = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 14px;
`;

const formatTime = (sec) => {
    if (!Number.isFinite(sec)) return '0.0s';
    return `${sec.toFixed(1)}s`;
};

/** Pick the first supported MediaRecorder mime for a re-encoded clip. */
const pickRecorderMime = () => {
    if (typeof MediaRecorder === 'undefined') return null;
    const candidates = [
        'video/mp4;codecs=avc1,mp4a.40.2',
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm',
    ];
    for (const t of candidates) {
        try { if (MediaRecorder.isTypeSupported(t)) return t; } catch { /* noop */ }
    }
    return null;
};

/**
 * Re-encode the [startSec, endSec] window of a source video File into a Blob
 * whose duration exactly matches the selected window. Uses captureStream on
 * the video element (with a canvas fallback for browsers that don't expose it).
 */
const trimVideoToBlob = async ({ file, startSec, endSec, sourceVideoEl }) => {
    const mimeType = pickRecorderMime();
    if (!mimeType || typeof MediaRecorder === 'undefined') {
        throw new Error('Your browser cannot trim videos. Please upload a clip 15 seconds or shorter.');
    }

    // Work on an isolated element so we don't disturb the visible preview.
    const src = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.src = src;
    video.crossOrigin = 'anonymous';
    video.muted = false;
    video.playsInline = true;
    video.preload = 'auto';

    try {
        await new Promise((resolve, reject) => {
            const onLoaded = () => { video.removeEventListener('loadedmetadata', onLoaded); resolve(); };
            video.addEventListener('loadedmetadata', onLoaded);
            video.addEventListener('error', () => reject(new Error('Could not load video for trimming')), { once: true });
        });

        // Seek to start.
        await new Promise((resolve) => {
            const onSeek = () => { video.removeEventListener('seeked', onSeek); resolve(); };
            video.addEventListener('seeked', onSeek);
            try { video.currentTime = Math.max(0, startSec); } catch { resolve(); }
        });

        let stream;
        if (typeof video.captureStream === 'function') {
            stream = video.captureStream();
        } else if (typeof video.mozCaptureStream === 'function') {
            stream = video.mozCaptureStream();
        } else {
            throw new Error('Your browser does not support in-place video trimming.');
        }

        const recorder = new MediaRecorder(stream, { mimeType });
        const chunks = [];
        recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };

        const stopped = new Promise((resolve) => { recorder.onstop = resolve; });
        recorder.start(100);

        const targetDurMs = Math.max(1, (endSec - startSec)) * 1000;
        try { await video.play(); } catch { /* autoplay policy — swallow */ }

        // Stop when we hit endSec OR the target duration elapses (whichever first).
        await new Promise((resolve) => {
            const started = performance.now();
            const check = () => {
                if (video.currentTime >= endSec - 0.05 || (performance.now() - started) >= targetDurMs) {
                    resolve();
                } else {
                    requestAnimationFrame(check);
                }
            };
            requestAnimationFrame(check);
        });

        try { video.pause(); } catch { /* noop */ }
        if (recorder.state !== 'inactive') recorder.stop();
        await stopped;
        stream.getTracks().forEach((t) => { try { t.stop(); } catch { /* noop */ } });

        const blob = new Blob(chunks, { type: mimeType });
        // Strip codec parameters (e.g. `video/mp4;codecs=avc1,mp4a.40.2`) — the
        // backend `isAllowedMediaType` compares against base MIME types only,
        // and MediaRecorder always emits codec-parameterized MIME strings.
        const baseMime = mimeType.split(';')[0].trim().toLowerCase();
        const ext = baseMime === 'video/mp4' ? 'mp4' : 'webm';
        const baseName = (file.name || 'vibe').replace(/\.[^.]+$/, '');
        return new File([blob], `${baseName}-trim.${ext}`, { type: baseMime });
    } finally {
        try { URL.revokeObjectURL(src); } catch { /* noop */ }
        // sourceVideoEl passed in for future canvas-fallback path; unused today.
        void sourceVideoEl;
    }
};

const VibeTrimmer = ({ file, maxSeconds, onCancel, onConfirm }) => {
    const videoRef = useRef(null);
    const timelineRef = useRef(null);
    const scrollRef = useRef(null);
    // Pointer drag-to-scroll state for the timeline. The scroller natively
    // handles wheel + touch pan; this ref tracks a mouse click-and-drag pan.
    const panRef = useRef(null);

    const [srcUrl, setSrcUrl] = useState(null);
    const [duration, setDuration] = useState(0);
    // Start/end are DERIVED from scrollLeft + pxPerSec; we still keep them in
    // state so the rest of the component (playback loop, meta, export) reads
    // the same source of truth.
    const [start, setStart] = useState(0);
    const [end, setEnd] = useState(0);
    const [playhead, setPlayhead] = useState(0);
    const [busy, setBusy] = useState(false);
    // Progress from ffmpeg during the trim (0..1). null while idle or when
    // the underlying tool doesn't emit progress.
    const [trimProgress, setTrimProgress] = useState(null);
    // Timeline zoom. `null` before first metadata load; then a pixels-per-second
    // number the user can adjust with +/− / Fit. Because the selection window
    // is now the full viewport width, pxPerSec also controls the selection's
    // duration: selectionSec = containerW / pxPerSec.
    const [pxPerSec, setPxPerSec] = useState(null);
    // Viewport width (of TimelineScroll) — drives the fixed selection length.
    const [containerW, setContainerW] = useState(0);
    // Filmstrip thumbnails — { time, url }[] rendered inside the timeline
    // as a Premiere-style preview strip. Generated once per source video via
    // an offscreen <video> + canvas snapshots.
    const [thumbnails, setThumbnails] = useState([]);
    // Reflects the underlying <video>.paused state so the play/pause overlay
    // stays in sync when playback is toggled externally (loop reset,
    // autoplay policy, etc.).
    const [paused, setPaused] = useState(true);

    const togglePlay = useCallback(() => {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) {
            // If we're outside the trim window, snap back to the start of
            // the selection before resuming.
            if (v.currentTime < start - 0.05 || v.currentTime >= end - 0.05) {
                try { v.currentTime = start; } catch { /* noop */ }
            }
            v.play().catch(() => { /* autoplay policy — swallow */ });
        } else {
            v.pause();
        }
    }, [start, end]);

    // Create + revoke object URL for the picked file.
    useEffect(() => {
        if (!file) return undefined;
        const url = URL.createObjectURL(file);
        setSrcUrl(url);
        return () => { try { URL.revokeObjectURL(url); } catch { /* noop */ } };
    }, [file]);

    const onLoadedMetadata = useCallback(() => {
        const v = videoRef.current;
        if (!v) return;
        const d = Number.isFinite(v.duration) ? v.duration : 0;
        setDuration(d);
        setStart(0);
        setEnd(Math.min(d, maxSeconds));
        setPlayhead(0);
        try { v.currentTime = 0; } catch { /* noop */ }
    }, [maxSeconds]);

    const onTimeUpdate = useCallback(() => {
        const v = videoRef.current;
        if (!v) return;
        setPlayhead(v.currentTime);
        // Loop within the trim window.
        if (v.currentTime >= end - 0.05) {
            try { v.currentTime = start; v.play().catch(() => {}); } catch { /* noop */ }
        } else if (v.currentTime < start - 0.05) {
            try { v.currentTime = start; } catch { /* noop */ }
        }
    }, [start, end]);

    // Track scroll container width so we can compute selection duration and
    // clamp zoom bounds.
    useEffect(() => {
        const el = scrollRef.current;
        if (!el || typeof ResizeObserver === 'undefined') {
            if (el) setContainerW(el.clientWidth);
            return undefined;
        }
        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerW(entry.contentRect.width);
            }
        });
        ro.observe(el);
        setContainerW(el.clientWidth);
        return () => ro.disconnect();
    }, []);

    // Initial zoom: pick pxPerSec so the selection window (= viewport) covers
    // exactly maxSeconds of video, or the whole clip if it's shorter.
    useEffect(() => {
        if (pxPerSec != null || duration <= 0 || containerW <= 0) return;
        const initialSelectionSec = Math.min(duration, maxSeconds);
        setPxPerSec(containerW / initialSelectionSec);
    }, [duration, containerW, maxSeconds, pxPerSec]);

    // Generate filmstrip thumbnails once per source video. Uses an offscreen
    // <video> seeked to N evenly-spaced timestamps + a canvas snapshot. We
    // publish incrementally so the strip fills in as it renders.
    useEffect(() => {
        if (!srcUrl || duration <= 0) return undefined;
        let cancelled = false;
        setThumbnails([]);

        const generate = async () => {
            // Aim for ~1 thumbnail per second, capped at 60 total.
            const targetCount = Math.min(60, Math.max(6, Math.round(duration)));
            const intervalSec = duration / targetCount;

            const v = document.createElement('video');
            v.src = srcUrl;
            v.muted = true;
            v.playsInline = true;
            v.preload = 'auto';
            v.crossOrigin = 'anonymous';

            await new Promise((resolve, reject) => {
                const onReady = () => { cleanup(); resolve(); };
                const onErr = () => { cleanup(); reject(new Error('thumb-video-load')); };
                const cleanup = () => {
                    v.removeEventListener('loadeddata', onReady);
                    v.removeEventListener('error', onErr);
                };
                v.addEventListener('loadeddata', onReady);
                v.addEventListener('error', onErr);
            });
            if (cancelled) return;

            const thumbW = 96;
            const ratio = v.videoWidth > 0 ? v.videoHeight / v.videoWidth : 9 / 16;
            const thumbH = Math.max(24, Math.round(thumbW * ratio));
            const canvas = document.createElement('canvas');
            canvas.width = thumbW;
            canvas.height = thumbH;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            const collected = [];
            for (let i = 0; i < targetCount; i += 1) {
                if (cancelled) return;
                const t = Math.min(Math.max(0, duration - 0.05), i * intervalSec);
                // eslint-disable-next-line no-await-in-loop
                await new Promise((resolve) => {
                    let done = false;
                    const finish = () => { if (done) return; done = true; v.removeEventListener('seeked', finish); resolve(); };
                    v.addEventListener('seeked', finish);
                    try { v.currentTime = t; } catch { finish(); }
                    // Safety timeout in case seeked never fires (some codecs).
                    setTimeout(finish, 400);
                });
                if (cancelled) return;
                try { ctx.drawImage(v, 0, 0, thumbW, thumbH); } catch { /* noop */ }
                let url = null;
                try { url = canvas.toDataURL('image/jpeg', 0.55); } catch { url = null; }
                if (url) {
                    collected.push({ time: t, url });
                    if (!cancelled) setThumbnails(collected.slice());
                }
            }
        };

        generate().catch(() => { /* thumbs are best-effort */ });
        return () => { cancelled = true; };
    }, [srcUrl, duration]);

    const totalTrackPx = duration > 0 && pxPerSec > 0 ? duration * pxPerSec : 0;

    // Derive start/end from scrollLeft. Listen to native scroll (which covers
    // wheel, touch swipe, mouse drag, and scrollbar dragging).
    useEffect(() => {
        const el = scrollRef.current;
        if (!el || !pxPerSec || duration <= 0 || containerW <= 0) return undefined;

        const sync = () => {
            const nextStart = Math.max(0, Math.min(duration, el.scrollLeft / pxPerSec));
            const selectionSec = Math.min(duration - nextStart, containerW / pxPerSec);
            const nextEnd = Math.min(duration, nextStart + selectionSec);
            setStart(nextStart);
            setEnd(nextEnd);
            // Seek preview to the new selection start when the user scrubs by
            // dragging, but don't fight ongoing playback while it's inside
            // the window.
            const v = videoRef.current;
            if (v && (v.currentTime < nextStart - 0.1 || v.currentTime > nextEnd + 0.1)) {
                try { v.currentTime = nextStart; } catch { /* noop */ }
            }
        };
        // Snap once when zoom/duration changes so selection is coherent.
        sync();
        el.addEventListener('scroll', sync, { passive: true });
        return () => el.removeEventListener('scroll', sync);
    }, [pxPerSec, duration, containerW]);

    // Mouse click-and-drag panning on the timeline (touch already works via
    // native `overflow-x: auto` + `touch-action: pan-x`).
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return undefined;

        const onDown = (e) => {
            // Ignore right-click, and only claim primary-button mouse events —
            // touch/pen events already have native panning.
            if (e.button !== 0 || e.pointerType === 'touch') return;
            panRef.current = { startX: e.clientX, startScroll: el.scrollLeft };
            el.setPointerCapture?.(e.pointerId);
        };
        const onMove = (e) => {
            if (!panRef.current) return;
            const dx = e.clientX - panRef.current.startX;
            el.scrollLeft = panRef.current.startScroll - dx;
        };
        const onUp = (e) => {
            if (!panRef.current) return;
            panRef.current = null;
            try { el.releasePointerCapture?.(e.pointerId); } catch { /* noop */ }
        };

        el.addEventListener('pointerdown', onDown);
        el.addEventListener('pointermove', onMove);
        el.addEventListener('pointerup', onUp);
        el.addEventListener('pointercancel', onUp);
        return () => {
            el.removeEventListener('pointerdown', onDown);
            el.removeEventListener('pointermove', onMove);
            el.removeEventListener('pointerup', onUp);
            el.removeEventListener('pointercancel', onUp);
        };
    }, []);

    // Zoom controls. Because selection duration = containerW / pxPerSec, we
    // clamp zoom so selection stays within [MIN_SECONDS, maxSeconds] (unless
    // the clip is shorter than maxSeconds, in which case fit is the min zoom).
    const zoomBy = (factor) => {
        if (!pxPerSec || duration <= 0 || containerW <= 0) return;
        const scroller = scrollRef.current;
        const anchorSec = scroller
            ? Math.min(duration, Math.max(0, scroller.scrollLeft / pxPerSec))
            : start;
        // Clamps:
        //   selectionSec must be ≥ MIN_SECONDS → pxPerSec ≤ containerW / MIN_SECONDS
        //   selectionSec must be ≤ min(maxSeconds, duration) → pxPerSec ≥ containerW / that
        const minPx = containerW / Math.min(maxSeconds, Math.max(duration, MIN_SECONDS));
        const maxPx = containerW / MIN_SECONDS;
        const nextPx = Math.max(minPx, Math.min(maxPx, pxPerSec * factor));
        setPxPerSec(nextPx);
        // Preserve the current start when zoom changes.
        if (scroller) {
            requestAnimationFrame(() => {
                if (!scrollRef.current) return;
                scrollRef.current.scrollLeft = anchorSec * nextPx;
            });
        }
    };

    const zoomToFit = () => {
        if (containerW <= 0 || duration <= 0) return;
        const nextPx = containerW / Math.min(duration, maxSeconds);
        setPxPerSec(nextPx);
        requestAnimationFrame(() => {
            if (scrollRef.current) scrollRef.current.scrollLeft = 0;
        });
    };

    // Explicit clip-length control (in seconds). Lets users trim to less
    // than maxSeconds without hunting through zoom controls. Preserves the
    // current selection start.
    const maxLengthSec = Math.min(maxSeconds, duration || maxSeconds);
    const applyLength = (nextLen) => {
        if (!containerW || duration <= 0) return;
        const clamped = Math.max(MIN_SECONDS, Math.min(maxLengthSec, nextLen));
        const nextPx = containerW / clamped;
        setPxPerSec(nextPx);
        requestAnimationFrame(() => {
            if (scrollRef.current) scrollRef.current.scrollLeft = start * nextPx;
        });
    };

    // Playhead is drawn inside the scrolled track — it stays visually tied to
    // its video-time position, moving with the film.
    const playheadPx = pxPerSec > 0 ? playhead * pxPerSec : 0;
    const windowSec = Math.max(0, end - start);
    const canConfirm = duration > 0 && windowSec >= MIN_SECONDS && !busy;

    // Ruler ticks.
    const rulerTicks = (() => {
        if (!pxPerSec || duration <= 0) return [];
        const targetPxBetweenLabels = 80;
        const rawStepSec = targetPxBetweenLabels / pxPerSec;
        const niceSteps = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600];
        const stepSec = niceSteps.find((s) => s >= rawStepSec) || Math.ceil(rawStepSec / 60) * 60;
        const ticks = [];
        for (let t = 0; t <= duration + 0.001; t += stepSec) {
            ticks.push(Math.min(duration, Math.round(t)));
        }
        return ticks;
    })();

    const formatRulerTick = (sec) => {
        const total = Math.round(sec);
        const m = Math.floor(total / 60);
        const s = total % 60;
        return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
    };

    const doConfirm = async () => {
        if (!canConfirm) return;
        setBusy(true);
        setTrimProgress(null);
        try {
            // Cap the exported window at maxSeconds even if the user's visual
            // selection is longer. We keep `start` as anchor and trim forward.
            const cappedEnd = Math.min(end, start + maxSeconds);

            // Primary path: ffmpeg.wasm — fast, exact, and always emits MP4.
            // If it can't be loaded (offline / CDN blocked / unsupported
            // browser) or fails at runtime, fall back to the MediaRecorder
            // path so users on flaky networks aren't stranded.
            let trimmed;
            try {
                trimmed = await ffmpegTrim({
                    file,
                    startSec: start,
                    endSec: cappedEnd,
                    onProgress: (p) => setTrimProgress(p),
                });
            } catch (ffmpegErr) {
                // eslint-disable-next-line no-console
                console.warn('[VibeTrimmer] ffmpeg trim failed, falling back to MediaRecorder', ffmpegErr);
                setTrimProgress(null);
                trimmed = await trimVideoToBlob({
                    file,
                    startSec: start,
                    endSec: cappedEnd,
                    sourceVideoEl: videoRef.current,
                });
            }
            onConfirm?.(trimmed, { startSec: start, endSec: cappedEnd });
        } catch (err) {
            const msg = err?.message || 'Could not trim video';
            // Bubble the error up so the composer can toast it.
            onCancel?.(msg);
        } finally {
            setBusy(false);
            setTrimProgress(null);
        }
    };

    return ReactDOM.createPortal(
        <Overlay>
            <Panel onClick={(e) => e.stopPropagation()}>
                <HeaderRow>
                    <Title>Trim your vibe · max {maxSeconds}s</Title>
                    <IconButton
                        $size="small"
                        onClick={() => onCancel?.()}
                        aria-label="Cancel trim"
                        disabled={busy}
                    >
                        <VipIcon name="CloseXCircle" size={22} />
                    </IconButton>
                </HeaderRow>

                <VideoFrame>
                    {srcUrl && (
                        <VideoBackdrop
                            src={srcUrl}
                            muted
                            autoPlay
                            playsInline
                            loop
                            aria-hidden="true"
                            tabIndex={-1}
                        />
                    )}
                    <video
                        ref={videoRef}
                        src={srcUrl || undefined}
                        onLoadedMetadata={onLoadedMetadata}
                        onTimeUpdate={onTimeUpdate}
                        onPlay={() => setPaused(false)}
                        onPause={() => setPaused(true)}
                        muted
                        autoPlay
                        playsInline
                        loop={false}
                    />
                    <PlayPauseBtn
                        type="button"
                        $paused={paused}
                        onClick={togglePlay}
                        aria-label={paused ? 'Play preview' : 'Pause preview'}
                        disabled={busy || !srcUrl}
                    >
                        <span>
                            <VipIcon name={paused ? 'Play' : 'Pause'} size={26} />
                        </span>
                    </PlayPauseBtn>
                </VideoFrame>

                <TimelineViewport>
                    <TimelineScroll ref={scrollRef}>
                        <Timeline
                            ref={timelineRef}
                            style={totalTrackPx > 0 ? { width: `${totalTrackPx}px` } : undefined}
                        >
                            {thumbnails.length > 0 && pxPerSec > 0 && (
                                <Filmstrip aria-hidden="true">
                                    {thumbnails.map((th, i) => {
                                        const nextT = thumbnails[i + 1]?.time ?? duration;
                                        const widthPx = Math.max(1, (nextT - th.time) * pxPerSec);
                                        return (
                                            <Thumb
                                                key={th.time}
                                                style={{
                                                    left: `${th.time * pxPerSec}px`,
                                                    width: `${widthPx}px`,
                                                    backgroundImage: `url(${th.url})`,
                                                }}
                                            />
                                        );
                                    })}
                                </Filmstrip>
                            )}
                            <Ruler>
                                {rulerTicks.map((t) => (
                                    <RulerTick
                                        key={t}
                                        style={{ left: `${t * pxPerSec}px` }}
                                    >
                                        {formatRulerTick(t)}
                                    </RulerTick>
                                ))}
                            </Ruler>
                            <Playhead style={{ left: `${playheadPx}px` }} />
                        </Timeline>
                    </TimelineScroll>

                    {/* Fixed selection band — pinned above the scroller.
                        Handles are decorative; the timeline drags underneath. */}
                    <SelectionOverlay
                        aria-label="Trim window"
                        role="group"
                        aria-valuemin={0}
                        aria-valuemax={duration}
                        aria-valuenow={Math.round(start * 10) / 10}
                    >
                        <SelectionBox />
                        <FixedHandle style={{ left: 0 }} aria-label="Selection start">
                            <VipIcon name="ChevronLeft" size={14} />
                        </FixedHandle>
                        <FixedHandle style={{ right: 0 }} aria-label="Selection end">
                            <VipIcon name="ChevronRight" size={14} />
                        </FixedHandle>
                    </SelectionOverlay>
                </TimelineViewport>

                <ZoomBar>
                    <span>Zoom</span>
                    <ZoomBtn
                        type="button"
                        onClick={() => zoomBy(1 / 1.5)}
                        aria-label="Zoom out"
                        disabled={!pxPerSec}
                    >−</ZoomBtn>
                    <FitBtn
                        type="button"
                        onClick={zoomToFit}
                        aria-label="Fit timeline"
                        disabled={!pxPerSec}
                    >Fit</FitBtn>
                    <ZoomBtn
                        type="button"
                        onClick={() => zoomBy(1.5)}
                        aria-label="Zoom in"
                        disabled={!pxPerSec}
                    >+</ZoomBtn>
                </ZoomBar>

                <LengthRow>
                    <span>Length</span>
                    <LengthSlider
                        min={MIN_SECONDS}
                        max={Math.max(MIN_SECONDS, maxLengthSec)}
                        step={0.1}
                        value={Math.min(Math.max(MIN_SECONDS, windowSec || MIN_SECONDS), maxLengthSec)}
                        onChange={(e) => applyLength(parseFloat(e.target.value))}
                        disabled={!pxPerSec || duration <= 0}
                        aria-label="Clip length in seconds"
                    />
                    <LengthValue>{formatTime(windowSec)}</LengthValue>
                </LengthRow>

                <Meta>
                    <span>Start {formatTime(start)}</span>
                    <span>{formatTime(windowSec)} selected</span>
                    <span>End {formatTime(end)}</span>
                </Meta>

                <ActionRow>
                    <SecondaryButton type="button" onClick={() => onCancel?.()} disabled={busy}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton type="button" onClick={doConfirm} disabled={!canConfirm}>
                        {busy ? (
                            <>
                                <InlineLoading size={16} color="#ffffff" />
                                <span>
                                    {trimProgress != null
                                        ? `Trimming… ${Math.round(trimProgress * 100)}%`
                                        : 'Trimming…'}
                                </span>
                            </>
                        ) : (
                            'Use clip'
                        )}
                    </PrimaryButton>
                </ActionRow>
            </Panel>
        </Overlay>,
        document.body
    );
};

export default VibeTrimmer;
